import { AccentPicker } from './AccentPicker';
import { CopyFields } from './CopyFields';
import { DecorationPicker } from './DecorationPicker';
import { ImagePicker } from './ImagePicker';
import { SendToField } from './SendToField';
import { countdownPreview } from './appCardMirror';
import { DURATIONS } from './bannerOptions';
import { FIELD_INPUT, FieldLabel, PillRow } from './bannerFormBits';
import type { BannerComposer } from './useBannerComposer';

/** A message pinned to people's home page: who, what, and how it looks. */
export function PersonalizedForm({ composer }: { composer: BannerComposer }) {
  const {
    audience,
    annDecoration,
    setAnnDecoration,
    annAccent,
    setAnnAccent,
    annVisibleDays,
    setAnnVisibleDays,
    annCountdownTo,
    setAnnCountdownTo,
    countdownDrivesVisibility,
  } = composer;
  return (
    <div className="grid gap-x-8 gap-y-4 xl:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-4">
        <SendToField audience={audience} />
        <CopyFields composer={composer} rows={7} />
      </div>

      <div className="flex min-w-0 flex-col gap-4">
        <div>
          <FieldLabel
            text="Decoration"
            hint="Animated particles that drift across the card. Pick None for anything serious."
          />
          <DecorationPicker compact value={annDecoration} onChange={setAnnDecoration} />
        </div>

        <AccentPicker value={annAccent} onChange={setAnnAccent} />

        <div>
          <FieldLabel text="Show for" />
          {countdownDrivesVisibility ? (
            // Not disabled buttons — a greyed-out row of choices still reads as "pick
            // one". State the single derived answer instead.
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              Until{' '}
              <strong>
                {new Date(annCountdownTo).toLocaleString(undefined, {
                  day: 'numeric',
                  month: 'short',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </strong>
              , plus a day.{' '}
              <button
                type="button"
                onClick={() => setAnnCountdownTo('')}
                className="font-medium text-primary-600 underline hover:no-underline"
              >
                Clear the countdown
              </button>{' '}
              to choose.
            </div>
          ) : (
            <PillRow
              options={DURATIONS.map((d) => ({ value: d.days, label: d.label }))}
              value={annVisibleDays}
              onChange={setAnnVisibleDays}
            />
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <FieldLabel
              text="Countdown"
              htmlFor="banner-countdown"
              hint={`Adds a ticking countdown to the card, for a launch, an event or a deadline. Optional. The card then stays until it ends, and one more day showing "It's here!".`}
            >
              {annCountdownTo && (
                <button
                  type="button"
                  onClick={() => setAnnCountdownTo('')}
                  className="flex-none text-xs font-medium text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </FieldLabel>
            <input
              id="banner-countdown"
              type="datetime-local"
              value={annCountdownTo}
              onChange={(e) => setAnnCountdownTo(e.target.value)}
              className={`${FIELD_INPUT} h-[38px]`}
            />
            {annCountdownTo && countdownPreview(annCountdownTo) === "It's here!" && (
              <p className="mt-1 text-xs text-amber-700">
                That date has already passed — pick a future one or the send will be rejected.
              </p>
            )}
          </div>
          <ImagePicker composer={composer} />
        </div>
      </div>
    </div>
  );
}
